import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getToken } from 'utils/token';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'utils/constants';

const PAYOUTS_URL = 'payouts';

export enum SellerPayoutStatus {
  Owed = 1,
  CodReceived = 2,
  Paid = 3
}

export interface ITransition {
  fromStatus: SellerPayoutStatus | null
  toStatus: SellerPayoutStatus
  changedByUserName: string
  note: string | null
  bankReference: string | null
  changedOn: string
}

export interface IPayout {
  id: number
  orderId: number
  shipmentId: string
  orderCreatedOn: string
  sellerUsername: string
  sellerIban: string | null
  sellerSwift: string | null
  amountOwed: number
  status: SellerPayoutStatus
  paidOn: string | null
  bankReference: string | null
  // -14 per OrdersProfitQuery.cs's DeliveredOperationCode - a hint the COD
  // has likely reached 2kiddo's account, not an automatic status change.
  operationCode: number
  transitions: ITransition[]
}

interface IPayoutsResponse {
  models: IPayout[]
  page: number
  totalPages: number
  totalCount: number
}

export const payoutApi = createApi({
  reducerPath: 'payout',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BASE_API_URL,
    prepareHeaders: (headers) => {
      const token = getToken();

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: ['Payouts'],
  endpoints: (builder) => ({
    getPayouts: builder.query<IPayoutsResponse, {
      status?: SellerPayoutStatus
      page?: number
      pageSize?: number
    }>({
      query: ({ status, page = DEFAULT_PAGE, pageSize = DEFAULT_PAGE_SIZE }) => {
        let queryString = `${PAYOUTS_URL}?page=${page}&pageSize=${pageSize}`;

        if (status) {
          queryString += `&status=${status}`;
        }

        return queryString;
      },
      providesTags: ['Payouts']
    }),
    transitionPayout: builder.mutation<null, {
      orderId: number
      toStatus: SellerPayoutStatus
      note?: string
      bankReference?: string
    }>({
      query: ({ orderId, ...body }) => ({
        url: `${PAYOUTS_URL}/${orderId}/transition`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['Payouts']
    }),
    exportPayouts: builder.mutation<null, { status?: SellerPayoutStatus }>({
      queryFn: async ({ status }, api, extraOptions, baseQuery) => {
        let queryString = `${PAYOUTS_URL}/Export`;

        if (status) {
          queryString += `?status=${status}`;
        }

        const result = await baseQuery({
          url: queryString,
          responseHandler: async response => await response.blob()
        });

        const hiddenElement = document.createElement('a');
        hiddenElement.href = window.URL.createObjectURL((result as any).data);
        hiddenElement.target = '_blank';
        hiddenElement.download = 'seller-payouts.xlsx';
        hiddenElement.click();

        return { data: null };
      }
    })
  })
});

export const {
  useGetPayoutsQuery,
  useTransitionPayoutMutation,
  useExportPayoutsMutation
} = payoutApi;
